import { defineComponent, ref, computed, onMounted, reactive } from 'vue'
import { KNOB_SIZE, RAIL_HEIGHT } from './constants'
import type { AllowedValueType, KnobLine, RangeInputProps } from './types'
import { useElementBounding, useElementSize, useResizeObserver } from '@vueuse/core'
import { FORM_INPUT_FIELD_PROPS, type RangeOptions } from '../input/types'
import Knob from './components/knob'
import { toNumber } from '@/utils/number'
import {
  VEC2,
  Vector,
  isNumber,
  isVector,
  range,
  isNumerical,
  isDigit,
  type AABB,
  VEC4
} from 'math'
import KnobTooltip from './components/knob-tooltip'
import { renderKnobLines } from './styling/knobLines'
import type { RenderLinesProps } from './styling/types'
import { renderRail } from './styling/rail'
import { useKnobs } from './hooks/useKnobs'
import { KnobObject } from './Knob'
import { computeKnobLines } from './utils'
import { until } from '@/utils/async/until'
import { useModalStore } from 'components/modal/store'
import { useEffect } from '@/utils/reactive/useEffect'
import { isNone, isNumberArray } from '@/utils/is'

const RangeInput = defineComponent<RangeInputProps>({
  name: 'RangeInput',
  setup(props) {


    const reportChange = (value: AllowedValueType) => {
      if (!K.isReady()) return
        const fieldRef = props.fieldRef
      const report = props.reportChange;

      if (props.onValueChanged) {
        props.onValueChanged(value);
      }

        if (fieldRef && report) {
          report(fieldRef, value)
        }
    }
    //const uidGen = UIDGenerator({
    //  uidLength: 24
    //})
    const wrapperRef = ref()
    const wrapperBounds = useElementBounding(wrapperRef)
    const wrapperSize = useElementSize(wrapperRef)

    const convertToOutput = (numbers: number[]): Vector | number | number[] => {
      if (numbers.length <= 0) {
        if (typeof props.value !== 'undefined') return props.value
        if (props.type && props.type.startsWith('vec')) return VEC4(0, 0, 0, 0)
        if (isNumberArray(props.value)) return props.value
        return 0
      }
      if (numbers.length === 1 && props.type === 'number') return numbers[0];
      if (numbers.length <= 4) return Vector.fromArray(numbers)
      return numbers
    }

    const numComponents = (() => {
      if (props.value && Array.isArray(props.value) && props.value.length > 0)
        return props.value.length

      if (props.type) {
        if (props.type === 'number') return 1

        if (props.type.startsWith('vec')) {
          const numpart = props.type.split('vec')[1]
          if (numpart && (isNumerical(numpart) || isDigit(numpart))) {
            const num = toNumber(numpart)
            if (num > 0) return num
          }
        }
      }
      if (typeof props.value !== 'undefined') {
        if (Array.isArray(props.value)) {
          return props.value.length
        }
        if (isVector(props.value)) {
          return 4
        }
      }
      return 1
    })()

    const initialValues = computed(() => {
      if (typeof props.value === 'number') return [props.value];
      const n = numComponents
      if (typeof props.value !== 'undefined') {
        if (Array.isArray(props.value)) {
          if (isNumberArray(props.value)) return props.value
          return props.value.map((it) => Number(it))
        }
        if (isVector(props.value)) return props.value.toArray(n > 0 ? n : 4)
        if (isNumber(props.value)) return [props.value]
      }
      return range(n).map(() => 0)
    })

    const knobRange = computed((): RangeOptions => {
      return {
        ...(props.range || {}),
        min: props.range?.min ?? 0,
        max: props.range?.max ?? 1,
        behaviour: props.range?.behaviour ?? 'default'
      }
    })

    const tooltips = computed(() => {
      const r = knobRange.value
      if (r.toolTips && r.toolTips.length > 0) return r.toolTips
      if (r.toolTip) return [r.toolTip]
      return []
    })

    const labels = computed(() => {
      const r = knobRange.value
      if (r.labels && r.labels.length > 0) return r.labels
      if (r.label) return [r.label]
      return []
    })

    const ranges = computed(() => {
      const getRanges = () => {
        const r = knobRange.value
        const children = r.ranges || []
        const count = numComponents
        if (children.length <= 0) return range(count).map(() => r)
        if (children.length < count)
          return [...children, ...range(count - children.length).map(() => r)]
        return children
      }

      const rs = getRanges().map((it, i) => {
        return {
          ...it,
          toolTip:
            tooltips.value.length <= 0 || i >= tooltips.value.length
              ? undefined
              : tooltips.value[i],
          label: labels.value.length <= 0 || i >= labels.value.length ? undefined : labels.value[i]
        }
      })

      return rs
    })

    const colors = computed(() => {
      return knobRange.value.colors || []
    })

    const bounds = computed(() => {
      const width = Math.max(wrapperBounds.width.value, wrapperSize.width.value)
      const height = Math.max(wrapperBounds.height.value, wrapperSize.height.value)
      return {
        min: VEC2(wrapperBounds.x.value, wrapperBounds.y.value),
        max: VEC2(width, height)
      }
    })

    const state = reactive<{
      values: number[]
      value: AllowedValueType
    }>({
      values: initialValues.value,
      value: convertToOutput(initialValues.value)
    })

    const K = useKnobs({
      bounds,
      knobs: [],
      count: initialValues.value.length,
      onKnobChange: (knob, _oldValue, nextValue) => {
        state.values[knob.index] = nextValue;
        const nextValues = state.values;
        if (isNone(nextValues)) return
        const output = convertToOutput(nextValues);
        if (typeof props.value !== 'undefined' && props.value !== null && typeof output !== typeof props.value) {
          console.warn(`Converted value is not of the expected type: ${typeof output} !== ${typeof props.value}`);
          console.warn({ output, propsValue: props.value })
          return;
        };
        state.value = output
        reportChange(output);
      }
    })

    const setupKnobs = async () => {
      if (K.knobs.value.length > 0) {
        K.clearKnobs()
      }
      await until(() => {
        wrapperBounds.update()
        return wrapperBounds.width.value > 1
      })
      K.push(
        initialValues.value.map((val, i) => {
          const uid = `knob-${i}`
          return new KnobObject({
            index: i,
            uid: uid,
            range: ranges.value[i],
            posRange: {
              min: VEC2(wrapperBounds.x.value, wrapperBounds.y.value),
              max: VEC2(wrapperBounds.width.value, wrapperBounds.height.value)
            },
            value: val,
            color: colors.value[i]
          })
        })
      )
    }

    const updateKnobBounds = () => {
      wrapperBounds.update()
      const width = Math.max(wrapperBounds.width.value, wrapperSize.width.value)
      const height = Math.max(wrapperBounds.height.value, wrapperSize.height.value)
      const nextBound: AABB = {
        min: VEC2(wrapperBounds.x.value, wrapperBounds.y.value),
        max: VEC2(width, height)
      }
      K.knobs.value.forEach((knob) => {
        knob.updatePositionRange(nextBound)
      })
    }

    useResizeObserver(wrapperRef, () => {
      updateKnobBounds()
    })

    const modalStore = useModalStore()

    useEffect(
      () => {
        updateKnobBounds()
      },
      [() => modalStore.animation],
      true,
      false,
      true
    )

    onMounted(() => {
      wrapperBounds.update()
      setupKnobs().catch((e) => console.error(e))

      setTimeout(() => {
        updateKnobBounds()
      }, 500)
    })

    //watch(
    //  () => state.value,
    //  () => {
    //    reportChange(state.value);
    //  }
    //)

    //watch(intersections, () => {

    //})

    const lines = computed((): KnobLine[] => {
      return computeKnobLines(K.knobs.value, bounds.value, colors.value)
    })

    const lineProps = computed((): RenderLinesProps => {
      return {
        range: knobRange.value,
        bounds: {
          min: VEC2(0, 0),
          max: VEC2(Math.max(1, wrapperBounds.width.value), wrapperBounds.y.value)
        }
      }
    })

    return () => {
      return (
        <div
          class="w-full relative py-3"
          ref={wrapperRef}
          style={{
            minHeight: RAIL_HEIGHT + KNOB_SIZE + 'px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div class="w-full relative">
            {renderRail(lines, lineProps.value)}
            {renderKnobLines(lines, lineProps.value)}
            {K.knobs.value.map((knob, i) => {
              return (
                <Knob
                  onVnodeMounted={(node) => knob.setupListeners(node)}
                  ref={K.knobsInfo.refs[i]}
                  key={`knob-${knob.uid}-${i}`}
                  item={knob.toItem()}
                />
              )
            })}
            {K.knobs.value.map((knob, i) => {
              return (
                <KnobTooltip
                  key={`knob-tooltip-${knob.uid}-${i}`}
                  knobRef={K.knobsInfo.refs[i]}
                  knob={knob}
                />
              )
            })}
          </div>
        </div>
      )
    }
  }
})
RangeInput.props = [...FORM_INPUT_FIELD_PROPS, 'onValueChanged', 'color']

export default RangeInput
